import { useMemo } from 'react';
import ModalBase from '@/components/base/ModalBase';
import type { AdminPost, PollOption } from '@/mocks/adminPosts';

interface PollResultsModalProps {
  open: boolean;
  post: AdminPost | null;
  onClose: () => void;
}

const BAR_COLORS = [
  'bg-emerald-500',
  'bg-teal-500',
  'bg-sky-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-violet-500',
  'bg-orange-500',
  'bg-cyan-500',
];

function PollOptionBar({ option, index, totalVotes, maxVotes }: {
  option: PollOption;
  index: number;
  totalVotes: number;
  maxVotes: number;
}) {
  const pct = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
  const relativeWidth = maxVotes > 0 ? (option.votes / maxVotes) * 100 : 0;
  const isWinner = totalVotes > 0 && option.votes === maxVotes;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isWinner && (
            <span className="shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
              <i className="ri-trophy-line text-amber-500 text-[11px]"></i>
            </span>
          )}
          <span className={`text-xs font-semibold truncate ${isWinner ? 'text-foreground-900' : 'text-foreground-700'}`}>
            {option.text}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] font-bold text-foreground-500 whitespace-nowrap">
            {option.votes.toLocaleString()} votos
          </span>
          <span className="text-[11px] font-bold text-foreground-400 w-9 text-right whitespace-nowrap">
            {pct}%
          </span>
        </div>
      </div>
      <div className="w-full h-2.5 rounded-full bg-background-200 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${BAR_COLORS[index % BAR_COLORS.length]} ${isWinner ? 'opacity-100' : 'opacity-75'}`}
          style={{ width: `${Math.max(relativeWidth, pct > 0 ? 3 : 0)}%` }}
        ></div>
      </div>
    </div>
  );
}

export default function PollResultsModal({ open, post, onClose }: PollResultsModalProps) {
  const stats = useMemo(() => {
    if (!post?.pollData) return null;
    const { options } = post.pollData;
    const totalVotes = options.reduce((sum, o) => sum + o.votes, 0);
    const maxVotes = Math.max(...options.map((o) => o.votes));
    const participants = options.filter((o) => o.votes > 0).length;
    return { totalVotes, maxVotes, participants };
  }, [post]);

  if (!post?.pollData) return null;

  const { pollData } = post;
  const sorted = [...pollData.options].sort((a, b) => b.votes - a.votes);

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Resultados de la encuesta"
      size="lg"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-background-100 text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
        >
          Cerrar
        </button>
      }
    >
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h3 className="text-base font-bold text-foreground-900 mb-1">{post.title}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
              pollData.isClosed
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}>
              <i className={`text-[9px] ${pollData.isClosed ? 'ri-lock-line' : 'ri-checkbox-circle-line'}`}></i>
              {pollData.isClosed ? 'Cerrada' : 'Abierta'}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background-200 text-foreground-500">
              <i className={pollData.pollType === 'single' ? 'ri-checkbox-circle-line' : 'ri-checkbox-multiple-line'}></i>
              {pollData.pollType === 'single' ? 'Una respuesta' : 'Varias respuestas'}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background-200 text-foreground-500">
              <i className="ri-group-line text-[9px]"></i>
              {stats?.totalVotes.toLocaleString()} votos totales
            </span>
          </div>
        </div>

        {/* Descripción */}
        {post.text && (
          <p className="text-sm text-foreground-500 leading-relaxed">{post.text}</p>
        )}

        {/* Barras */}
        <div className="space-y-4">
          {sorted.map((option, i) => (
            <PollOptionBar
              key={option.id}
              option={option}
              index={i}
              totalVotes={stats?.totalVotes || 0}
              maxVotes={stats?.maxVotes || 0}
            />
          ))}
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-background-200">
          <div className="text-center">
            <p className="text-xl font-bold text-foreground-900">{stats?.totalVotes.toLocaleString()}</p>
            <p className="text-[10px] text-foreground-400 font-medium">Votos totales</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-foreground-900">{pollData.options.length}</p>
            <p className="text-[10px] text-foreground-400 font-medium">Opciones</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-foreground-900">
              {sorted.length > 0 ? `${Math.round((sorted[0].votes / Math.max(stats?.totalVotes || 1, 1)) * 100)}%` : '0%'}
            </p>
            <p className="text-[10px] text-foreground-400 font-medium">Opción líder</p>
          </div>
        </div>
      </div>
    </ModalBase>
  );
}